/** What the register says when the year-on-year bridge opened it on a list of sites.
 *
 * The bridge's boundary-change and unexplained totals are sums of several sites' movements. They
 * open the register on exactly those sites (`sites` in the URL, `search.ts`), and this is the
 * sentence that tells the reader what they are looking at, in the register's own words.
 *
 * **Everything printed here is derived, nothing is carried in the link.** The sites are the URL's,
 * the figure is summed from the comparison's own movements for those sites (so it is the bridge's
 * cell, not a number a link could misquote), and the count of sites that have a figure in this
 * window comes from the register. The link names which sites and nothing else.
 *
 * Honest states, each one a sentence rather than a silence:
 *
 * - **A site with no figure in this window is said to be missing from it**, by name, because the
 *   register drops a site it could not have offered and a reader shown fewer rows than sites would
 *   conclude figures were lost. The other window is offered, since a site sold last year has
 *   figures there and none here.
 * - **Nothing is claimed about a total the comparison cannot confirm.** Without the comparison (it
 *   is still loading, failed, or the link names a site the bridge does not list) the sentence says
 *   how many sites and stops; it never prints a figure it could not derive.
 */

import { sumDecimals } from "../lib/decimal";
import type { BridgeInput, BridgeSite } from "./bridgeModel";
import { bridgeCells } from "./bridgeModel";
import { signedTonnes } from "./reportsFormat";

/** The comparison, as far as the banner reads it. */
export interface BannerComparison extends Omit<BridgeInput, "movements"> {
  movements: readonly BridgeSite[];
  prior_start: string;
  prior_end: string;
  current_start: string;
  current_end: string;
}

/** Whether these sites are the ones a boundary change explains, the ones none explains, or a mix
 * (a hand-edited link): the words that go with the figure depend on it. */
export type Basis = "explained" | "unexplained" | "mixed";

export interface SiteBanner {
  /** How many sites the link names. */
  asked: number;
  /** How many of them have a figure in the window on screen. */
  shown: number;
  /** The sites that have none, by name. A site the screen cannot name is said to be unnamed. */
  missing: string[];
  /** The movement of the named sites against last year, signed, at the bridge's own precision;
   * undefined when the comparison cannot confirm it. */
  sum?: string;
  basis?: Basis;
  /** The other of the two windows the bridge compares, when the register is on one of them. */
  other?: { which: "last year" | "this year"; from: string; to: string };
}

export const UNNAMED_SITE = "a site this screen cannot name";

export function siteBanner(input: {
  asked: readonly string[];
  shown: readonly string[];
  names: ReadonlyMap<string, string>;
  comparison: BannerComparison | undefined;
  from: string | undefined;
  to: string | undefined;
}): SiteBanner {
  const { asked, shown, names, comparison, from, to } = input;
  const have = new Set(shown);
  const banner: SiteBanner = {
    asked: asked.length,
    shown: asked.filter((id) => have.has(id)).length,
    missing: asked.filter((id) => !have.has(id)).map((id) => names.get(id) ?? UNNAMED_SITE),
  };
  if (!comparison) return banner;

  const byId = new Map(comparison.movements.map((movement) => [movement.facility_id, movement]));
  const listed = asked.map((id) => byId.get(id));
  if (asked.length > 0 && listed.every((movement) => movement !== undefined)) {
    const movements = listed;
    const total = sumDecimals(movements.map((movement) => movement.delta_tonnes));
    if (total !== undefined) {
      // The bridge's own precision, so the figure here is the cell there.
      banner.sum = signedTonnes(total, bridgeCells(comparison).places);
      const explained = movements.filter((movement) => Boolean(movement.because)).length;
      banner.basis =
        explained === movements.length ? "explained" : explained === 0 ? "unexplained" : "mixed";
    }
  }

  if (from === comparison.current_start && to === comparison.current_end) {
    banner.other = { which: "last year", from: comparison.prior_start, to: comparison.prior_end };
  } else if (from === comparison.prior_start && to === comparison.prior_end) {
    banner.other = { which: "this year", from: comparison.current_start, to: comparison.current_end };
  }
  return banner;
}
